import React from "react";
import { useNavigate } from "react-router-dom";
import "react-toastify/dist/ReactToastify.css";
import DataTable, { createTheme } from "react-data-table-component";
import { useTheme } from "../../../context/ThemeContext";
import TableLoader from "../../skeletons/TableLoader";
import { checkPermission } from "../../../helpers/GeneralHelper";
import { utcToLocal } from "../../../helpers/DateHelper";
import HelpTooltip from "../../common/HelpTooltip";
import {
  buildPipelinePrefillFromRequirement,
  buildPipelineSearchParams,
} from "../../../helpers/PipelineFilterHelper";
import { getCmsDataTableStyles } from "../../../helpers/DataTableStyles";

export default function RequirementTable({
  module,
  data,
  loading,
  totalRows,
  page,
  limit,
  fetchData,
  openUpdateModal,
  openDeleteModal,
  handleView,
  isTrash,
}) {
  const navigate = useNavigate();

  const goWithRequirement = (row, path) => {
    if (isTrash) return;
    const prefill = buildPipelinePrefillFromRequirement(row);
    navigate(`${path}?${buildPipelineSearchParams(row)}`, {
      state: prefill ? { pipelinePrefill: prefill } : undefined,
    });
  };

  const clickableCountCell = (row, value, display, path, title) => (
    <button
      type="button"
      className="btn btn-link p-0 text-decoration-none fw-bold lh-1 mt-2"
      style={{ fontSize: "1.25rem" }}
      title={title}
      onClick={() => goWithRequirement(row, path)}
      disabled={isTrash}
    >
      {display}
    </button>
  );

  const columns = [
    {
      name: "#",
      selector: (row, index) => <div>{ ((page -1 ) * limit) + index +1 }.</div>,
      width: '100px'
    },
    {
      name: "Requirement Name",
      selector: (row) => row.requirement_name,
      sortable: true, // ✅ enable sorting
      wrap: true,
      style: {
        whiteSpace: 'normal',
        wordBreak: 'break-word',
      },
    },
    {
      name: "Client",
      selector: (row) => row.client_name,
      sortable: true, // ✅ enable sorting
      wrap: true,
      style: {
        whiteSpace: 'normal',
        wordBreak: 'break-word',
      },
    },
    {
      name: "POC",
      selector: (row) => row.contact_name,
      sortable: true, // ✅ enable sorting
      wrap: true,
      style: {
        whiteSpace: 'normal',
        wordBreak: 'break-word',
      },
    },
    {
      name: "Manager",
      selector: (row) => row.manager_names,
      sortable: true, // ✅ enable sorting
      wrap: true,
      style: {
        whiteSpace: 'normal',
        wordBreak: 'break-word',
      },
    },
    {
      name : "Type",
      selector : (row) => {
        if(row.requirement_type==='c2h') return "Contract to Hire";
        if(row.requirement_type==='fte') return "Fulltime Hiring";
        return 'N/A';
      },
      sortable: true, // ✅ enable sorting
      wrap: true,
      style: {
        whiteSpace: 'normal',
        wordBreak: 'break-word',
      },
    },
    // {
    //   name: "Top 3 Skills",
    //   selector: (row) => row.top_3_skills
    // },
    {
      name: "Status",
      selector: (row) => row.status_name,
      sortable: true, // ✅ enable sorting
    },
    {
      name: (
        <span>
          Counts
          <HelpTooltip text="Click Submissions or Interviews to open a filtered list for this requirement." />
        </span>
      ),
      width: "208px",
      minWidth: "198px",
      maxWidth: "248px",
      sortable: false,
      wrap: true,
      cell: (row) => {
        const submitted = Number(row.candidates_submitted_count);
        const interviews = Number(row.interviews_count);
        const positions = Number(row.positions_count);
        const available = Number(row.positions_available_count);
        const submittedDisplay = Number.isFinite(submitted) ? submitted : 0;
        const interviewsDisplay = Number.isFinite(interviews) ? interviews : 0;
        const positionsDisplay = Number.isFinite(positions) ? positions : 0;
        const availableDisplay = Number.isFinite(available) ? available : 0;
        const countLabelStyle = {
          fontSize: "0.52rem",
          letterSpacing: "0.02em",
          lineHeight: 1.1,
          whiteSpace: "nowrap",
        };
        const countCellBase =
          "text-center py-2 px-2 d-flex flex-column align-items-center justify-content-center";
        const countBorder = "border-secondary border-opacity-25";

        return (
          <div className="w-100 py-2 px-1">
            <div
              className="rounded-3 border shadow-sm mx-auto overflow-hidden"
              style={{
                maxWidth: "232px",
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gridTemplateRows: "1fr 1fr",
              }}
            >
              <div
                className={`${countCellBase} bg-primary bg-opacity-10 border-end ${countBorder} border-bottom ${countBorder}`}
              >
                <div className="text-uppercase text-muted fw-semibold text-nowrap" style={countLabelStyle}>
                  Positions
                </div>
                <div className="fs-5 fw-bold text-primary lh-1 mt-2">{positionsDisplay}</div>
              </div>
              <div className={`${countCellBase} bg-success bg-opacity-10 border-bottom ${countBorder}`}>
                <div className="text-uppercase text-muted fw-semibold text-nowrap" style={countLabelStyle}>
                  Available
                </div>
                <div className="fs-5 fw-bold text-success lh-1 mt-2">{availableDisplay}</div>
              </div>
              <div className={`${countCellBase} bg-info bg-opacity-10 border-end ${countBorder}`}>
                <div className="text-uppercase text-muted fw-semibold text-nowrap" style={countLabelStyle}>
                  Submissions
                </div>
                {clickableCountCell(
                  row,
                  submitted,
                  submittedDisplay,
                  "/candidates",
                  "View candidates submitted for this requirement"
                )}
              </div>
              <div className={`${countCellBase} bg-warning bg-opacity-10`}>
                <div className="text-uppercase text-muted fw-semibold text-nowrap" style={countLabelStyle}>
                  Interviews
                </div>
                {clickableCountCell(
                  row,
                  interviews,
                  interviewsDisplay,
                  "/interviews",
                  "View interviews for this requirement"
                )}
              </div>
            </div>
          </div>
        );
      },
    },
    // {
    //   name: "Remarks",
    //   selector: (row) => row.requirement_remarks,
    //   sortable: true, // ✅ enable sorting
    // },
    {
      name: "Added On",
      selector: (row) => utcToLocal(row.requirement_create_time),
      sortable: true, // ✅ enable sorting
      wrap: true,
      style: {
        whiteSpace: 'normal',
        wordBreak: 'break-word',
      }, 
    },
    // {
    //   name: "Last Edited On",
    //   selector: (row) => utcToLocal(row.requirement_update_time)
    // }
  ];

  if (! isTrash && (checkPermission(module.route, 'edit') || checkPermission(module.route, 'delete'))) {
    columns.push({
      name: "Action",
      right: "true",
      cell: row => <div className="hstack gap-2 fs-15">
                    {checkPermission(module.route, 'edit') && <button type="button" className="btn btn-icon btn-sm btn-info" title="Edit" onClick={() => openUpdateModal(row)}>
                      <i className='bx bxs-edit'></i>
                    </button>}

                    {checkPermission(module.route, 'delete') && <button type="button" className="btn btn-icon btn-sm btn-danger" title="Delete" onClick={() => openDeleteModal(row)}>
                      <i className='bx bxs-trash'></i>
                    </button>}
                  </div>
    });
  }

  if (isTrash) {
    columns.push({
      name: "Deleted At",
      selector: (row) => utcToLocal(row.requirement_delete_time)
    });
  }

  const { theme, toggleTheme } = useTheme();

  const handlePageChange = async (page) => {
    fetchData(limit, page);
  };

  const handleLimitChange = async (newLimit, page) => {
    //if ((newLimit !== 10 && page !== 1) || localStorage.getItem(module.key)) {
      fetchData(newLimit, page);
    //}
  };

  // useEffect(() => {
  //   fetchData(limit, page);
  // }, []);

  createTheme("dark", {
    text: {
      primary: "#a2a3a4",
      secondary: "#2aa198",
    },
    background: {
      default: "#252729",
    },
    context: {
      background: "#cb4b16",
      text: "#a2a3a4",
    },
    divider: {
      default: "#073642",
    },
    action: {
      button: "rgba(0,0,0,.54)",
      hover: "rgba(0,0,0,.08)",
      disabled: "rgba(0,0,0,.12)",
    },
  });

  return (
    <DataTable
      // title={`Manage Notes (Private)`}
      className="clickable cms-data-table"
      onRowClicked={(row) => handleView(row)}
      columns={columns}
      data={data}
      pagination
      paginationServer
      paginationTotalRows={totalRows}
      onChangeRowsPerPage={handleLimitChange}
      onChangePage={handlePageChange}
      progressPending={loading}
      progressComponent={<TableLoader noOfColumn={columns.length} />}
      fixedHeader
      fixedHeaderScrollHeight="550px"
      highlightOnHover
      paginationPerPage={10}  // default page size
      paginationRowsPerPageOptions={[10, 25, 50, 100, totalRows]} // 👈 added custom options
      theme={`${theme === "light" ? "light" : "dark"}`}
      customStyles={getCmsDataTableStyles(theme)}
    />
  );
};
